import { AsyncPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Dossier } from '@sc/data-access-models';
import {
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import {
  TabTitleStrategyService,
  TranslatorService,
} from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import {
  AppointmentSubscriptionBuilder,
  AppointmentsSubService,
} from '@sc/feature-appointments-subscription';
import { AuthService } from '@sc/feature-auth-okta';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { InsurerFooterComponent } from '@sc/feature-insurer-footer';
import { NavBarComponent } from '@sc/feature-insurer-navbar';
import { RegisterAppService } from '@sc/register-app-actions';
import {
  ButtonComponent,
  StepperComponent,
  StepperStep,
} from '@sc/ui-components';
import { DeclarationConditions } from '@sc/utils';
import { BehaviorSubject, EMPTY, Observable, Subscription, of } from 'rxjs';
import { delayWhen, map, switchMap, take, tap } from 'rxjs/operators';
import { InsurerDeclarationSteps } from '../../models';
import { CustomerDataFormComponent } from './components';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import { InsightService } from '@sc/data-access-http-services';
import { InsurerRouterService } from '@sc/feature-insurer-path-router-system';

@Component({
  selector: 'sc-insurer-declaration-container',
  styleUrls: ['./declaration.container.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AppointmentSubscriptionBuilder],
  imports: [
    NavBarComponent,
    NgIf,
    StepperComponent,
    ButtonComponent,
    RouterOutlet,
    InsurerFooterComponent,
    AsyncPipe,
    TranslateModule,
  ],
  template: `
    <header role="banner">
      <sc-insurer-navbar
        (initDossierButtonClickEvent)="onInitDossierButton()"
        [currentDossier]="currentDossier"
        [userAllowedToAdminPage]="userAllowedAdmin | async"
        [userAllowedToDashboardPage]="userAllowedDashboard | async"
        [userAllowedToPartnerFitterPage]="userAllowedPartnerFitter | async"
        [userAllowedToDossiersSearchPage]="userAllowedDossiersSearch | async"
      ></sc-insurer-navbar>
    </header>
    <main role="main">
      <span
        class="action-dossier-nav"
        *ngIf="(sandbox.currentStep$ | async).step && showStepperAndAbandon"
      >
        <sc-stepper
          [disabled]="(sandbox.currentStep$ | async).disabled"
          [selectedIndex]="(sandbox.currentStep$ | async).step"
          [steps]="steps"
        ></sc-stepper>

        <sc-button
          class="customer-data-form-button"
          id="sendCustomerLinkButton"
          *ngIf="
            featureManager.isFeatureActivated('send-customer-access-email')
          "
          [disabled]="!canSendCustomerLink()"
          buttonType="secondary"
          (click)="onSendLink()"
          >{{ 'CustomerDataForm.sendLinkButton' | translate }}</sc-button
        >
      </span>
      <router-outlet
        id="sc-container-id"
        (activate)="onActivate($event)"
      ></router-outlet>
    </main>
    <footer role="contentinfo" class="sc-conf-display-b2b-footer">
      <sc-insurer-footer></sc-insurer-footer>
    </footer>
  `,
})
export class DeclarationContainerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  currentComponent: Component = null;
  public userAllowedDashboard: Observable<boolean>;
  public userAllowedAdmin: Observable<boolean>;
  public userAllowedPartnerFitter: Observable<boolean>;
  public userAllowedDossiersSearch: Observable<boolean>;
  public currentDossier: Dossier = null;

  canEditDossierFromApiCreation = true;

  steps: StepperStep[] = [
    {
      titleShort: 'InsurerStepper.identificationInsuredStep.short',
      titleLong: 'InsurerStepper.identificationInsuredStep.long',
      action: () =>
        this.router
          .navigate(['/declaration/customer-data'])
          .catch((error) => console.error(error)),
    },
    {
      titleShort: 'InsurerStepper.claimFolderStep.short',
      titleLong: 'InsurerStepper.claimFolderStep.long',
      action: () =>
        this.router
          .navigate(['/declaration/sinister-data'])
          .catch((error) => console.error(error)),
    },
    {
      titleShort: 'InsurerStepper.interventionStep.short',
      titleLong: 'InsurerStepper.interventionStep.long',
      action: () => {},
    },
    {
      titleShort: 'InsurerStepper.summaryStep.short',
      titleLong: 'InsurerStepper.summaryStep.long',
      action: () => {},
    },
  ];
  insurerSub: Subscription;
  private readonly destroy$ = new BehaviorSubject<boolean>(true);
  showStepperAndAbandon = true;

  appointmentSubscription: AppointmentsSubService;
  dossierSubscription: Subscription;
  stepperSubscription: Subscription;

  isInsurer = false;

  constructor(
    private readonly dossierSandboxService: DossierSandboxService,
    private readonly appointmentSubBuilder: AppointmentSubscriptionBuilder,
    public sandbox: SandboxService,
    public readonly featureManager: FeatureFlippingManagerService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly overlayDialogService: AdaptableOverlayService,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly route: ActivatedRoute,
    private readonly tabTitleStrategy: TabTitleStrategyService,
    private readonly insightService: InsightService,
    private readonly insurerRouterService: InsurerRouterService,
    private readonly translator: TranslatorService,
    private readonly registerAppService: RegisterAppService
  ) {
    this.userAllowedAdmin = this.authService.userHasInsurerAdminAccess$();
    this.userAllowedDashboard =
      this.authService.userHasInsurerDashboardAccess$();
    this.userAllowedPartnerFitter =
      this.authService.userHasInsurerPartnerFitterAccess$();
    this.userAllowedDossiersSearch =
      this.authService.userHasClaimsListAccess$();
    this.authService.userIsInsurer$().subscribe((isInsurer) => {
      this.isInsurer = isInsurer;
    });
  }
  ngAfterViewInit(): void {
    this.destroy$.next(false);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams?.forceNewDossier) {
        this.clearStore();
      }
    });

    this.stepperSubscription = this.sandbox.currentStep$.subscribe(
      (newStep) => {
        this.showStepperAndAbandon =
          newStep.step !== InsurerDeclarationSteps.summaryStep;
        if (newStep.step === 0) {
          this.tabTitleStrategy.setStepperInfos({ key: null });
        } else {
          this.tabTitleStrategy.setStepperInfos({
            key: 'GlobalApplicativeConstants.tabTitles.stepperInfos',
            params: {
              currentStep: newStep.step,
              numberOfSteps: this.steps.length,
              stepperTitle: this.declarationConditions.hasBeenBookedOrRequested(
                this.currentDossier
              )
                ? this.translator.getInstantTranslation(
                    'CustomerStepper.finalStep'
                  )
                : this.translator.getInstantTranslation(
                    this.steps[newStep?.step - 1]?.titleLong
                  ),
            },
          });
        }
      }
    );

    this.dossierSubscription = this.dossierSandboxService.dossier$.subscribe(
      (dossier) => {
        this.currentDossier = dossier;
        this.canEditDossierFromApiCreation =
          this.declarationConditions.canEditB2BDossierAfterApiCreation(
            this.currentDossier
          );
        this.steps[0].disabled = !this.canEditDossierFromApiCreation;
        this.steps[1].disabled = !this.canEditDossierFromApiCreation;

        if (
          this.authService.getOktaFlow() &&
          this.destroy$.getValue() &&
          this.isInsurer &&
          !!dossier.id
        ) {
          const url =
            this.insurerRouterService.getRedirectUrlFromDossier(dossier);
          this.router.navigateByUrl(url).catch((error) => console.error(error));
        }
      }
    );

    this.appointmentSubscription = this.appointmentSubBuilder.build();

    this.registerAppService.startUnloadListener();
  }

  ngOnDestroy(): void {
    this.stepperSubscription?.unsubscribe();
    this.dossierSubscription?.unsubscribe();
    this.appointmentSubscription?.destroy();
    this.tabTitleStrategy.setStepperInfos(null);
    this.destroy$.complete();
    this.registerAppService.stopUnloadListener();
  }

  onActivate(component: Component): void {
    this.currentComponent = component;
  }

  clearStore(): void {
    this.authService.clearCurrentDossierId();
    this.dossierSandboxService.clearDossier();
    this.appointmentSubscription.clearAll();
    if (
      this.currentComponent &&
      this.currentComponent instanceof CustomerDataFormComponent
    ) {
      (this.currentComponent as CustomerDataFormComponent)?.generateForm(true);
    }
  }

  canSendCustomerLink(): boolean {
    return this.currentComponent &&
      this.currentComponent instanceof CustomerDataFormComponent
      ? (this.currentComponent as CustomerDataFormComponent).hasValidForm()
      : true;
  }

  onInitDossierButton(): void {
    this.clearStore();
    this.authService
      .isCurrentDossierAuth$()
      .pipe(take(1))
      .subscribe((isDossierAuth) => {
        if (isDossierAuth) {
          this.router
            .navigate(['/login'])
            .catch((error) => console.error(error));
        } else if (this.router.url !== '/declaration/customer-data') {
          this.router
            .navigate(['/declaration/customer-data'])
            .catch((error) => console.error(error));
        }
      });
  }

  abandonDossier() {
    let currentRoute = this.router.url;
    this.overlayDialogService
      .showOverlay({ type: 'dossier-abandon', input: null })
      .subscribe((result) => {
        let onAbandon$ = of(false);
        if (result?.type === 'success') {
          onAbandon$ = this.dossierSandboxService.dossier$.pipe(
            take(1),
            switchMap((dossier) =>
              this.dossierSandboxService
                .abandonDossier({
                  id: dossier.id,
                  abandonmentComment: result.abandonmentComment,
                })
                .pipe(
                  switchMap(() =>
                    this.dossierSandboxService.patchDossier({
                      insurerData: {
                        ...dossier.insurerData,
                        abandonmentComment: result.abandonmentComment,
                      },
                      state: 'ABANDONED',
                    })
                  )
                )
            ),
            map(() => {
              currentRoute = '/declaration/declaration-end';
              return false;
            })
          );
        } else if (result?.type === 'later') {
          onAbandon$ = this.dossierSandboxService.dossier$.pipe(
            take(1),
            switchMap((dossier) =>
              this.dossierSandboxService.patchDossier({
                insurerData: {
                  ...dossier.insurerData,
                  finishLaterComment: result.finishLaterComment,
                },
              })
            ),
            switchMap(() => this.dossierSandboxService.updateDossier()),
            switchMap((dossier) =>
              this.dossierSandboxService.setDossier(dossier)
            ),
            map(() => true)
          );
        }

        onAbandon$.subscribe((shouldInitNewDossier) => {
          if (shouldInitNewDossier) {
            this.onInitDossierButton();
          } else {
            this.router
              .navigate([currentRoute])
              .catch((error) => console.error(error));
          }
        });
      });
  }

  canAbandonDossier() {
    return (
      this.currentDossier?.id &&
      this.dossierSandboxService.statesComplete.findIndex(
        (state) => state === this.currentDossier?.state
      ) === -1 &&
      this.currentDossier?.state !== 'ABANDONED' &&
      this.featureManager.isFeatureActivated('b2b-allow-abandon-files')
    );
  }

  onSendLink(): void {
    this.onCreateOrUpdateDossier()
      .pipe(
        take(1),
        switchMap((dossier: Dossier) => {
          return this.dossierSandboxService.sendCustomerAccessEmail(dossier.id);
        })
      )
      .subscribe({
        next: (dossier: Dossier) => {
          this.loadingService.setLoading(false),
            (this.currentDossier = dossier);
          this.authService.setCurrentDossierId(dossier.id);
          this.snackbarService.showSuccess(
            this.translator.getInstantTranslation(
              'CustomerDataForm.smsMailSentSuccess'
            )
          );
        },
        error: (error) => {
          console.error(error);
          this.loadingService.setLoading(false),
            this.snackbarService.showError(
              this.translator.getInstantTranslation('CustomerDataForm.error'),
              this.translator.getInstantTranslation(
                'CustomerDataForm.errorMessage'
              )
            );
        },
      });
  }

  onCreateOrUpdateDossier(): Observable<Partial<Dossier>> {
    if (this.canSendCustomerLink()) {
      const dossierFromForm =
        this.currentComponent instanceof CustomerDataFormComponent
          ? {
              ...this.currentDossier,
              ...(
                this.currentComponent as CustomerDataFormComponent
              ).formGroupToDossier(),
            }
          : this.currentDossier;
      this.loadingService.setLoading(true);

      return this.dossierSandboxService.dossier$.pipe(
        take(1),
        switchMap((dossier: Dossier) => {
          if (dossier?.id) {
            const updatedDossier = {
              ...dossierFromForm,
              id: dossier.id,
              claim: {
                ...dossierFromForm.claim,
                glass: dossier.claim.glass,
                sinisterPlace: dossier.claim.sinisterPlace,
                documents: dossier.claim.documents,
              },
              appointment: null,
            };

            return this.dossierSandboxService.setDossier(updatedDossier).pipe(
              switchMap(() => {
                return this.dossierSandboxService.updateDossier();
              })
            );
          } else {
            return this.createDossier(dossierFromForm).pipe(
              tap((createdDossier: Dossier) => {
                this.insightService.trackEvent({
                  name: `FrontApp [Insurer-Action] start new Dossier`,
                  properties: {
                    createdDossier,
                  },
                });
                this.currentDossier = createdDossier;
                this.authService.setCurrentDossierId(createdDossier.id);
              }),
              delayWhen((createdDossier) => {
                return this.dossierSandboxService.setDossier({
                  ...createdDossier,
                  id: createdDossier.id,
                });
              })
            );
          }
        })
      );
    }
    return EMPTY;
  }

  private createDossier(
    dossier: Partial<Dossier>
  ): Observable<Partial<Dossier>> {
    this.dossierSandboxService.setDossier(dossier);
    return this.dossierSandboxService.createDossierAccount();
  }
}
